///
/// EarthBrowser
///
/// Created by Matt Giger on 1/3/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"

export interface AuroraProperties {
	forecast_time: string
	data_url: string
	min_value: number
	max_value: number
	vertex_count: number
	grid_resolution: number
}

const cBatchSize = 100

export class AuroraTable {
	public static async create(db: Database): Promise<void> {
		// Migrate from old schema that had embedded binary_data
		const columns = await db.exec("pragma table_info(aurora)", [], "low") as Array<{ name: string }>
		if (columns.some(c => c.name === "binary_data")) {
			await db.exec("drop table aurora", [], "critical")
		}

		await db.execMany([
			{
				sql: `create table if not exists aurora (
					forecast_time text primary key,
					data_url text not null,
					min_value real not null default 0,
					max_value real not null default 100,
					vertex_count integer not null,
					grid_resolution integer not null default 3
				)`
			},
			{ sql: "create index if not exists idx_aurora_forecast_time on aurora(forecast_time desc)" },
		])
	}

	public static async replaceAll(rows: AuroraProperties[]): Promise<void> {
		await Database.db.exec("delete from aurora", [], "critical")
		await this.upsert(rows)
	}

	public static async upsert(rows: AuroraProperties[]): Promise<void> {
		const statement = `insert into aurora (forecast_time, data_url, min_value, max_value, vertex_count, grid_resolution)
			values (?, ?, ?, ?, ?, ?)
			on conflict(forecast_time) do update set
				data_url = excluded.data_url,
				min_value = excluded.min_value,
				max_value = excluded.max_value,
				vertex_count = excluded.vertex_count,
				grid_resolution = excluded.grid_resolution`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(a => [
				a.forecast_time,
				a.data_url,
				a.min_value,
				a.max_value,
				a.vertex_count,
				a.grid_resolution,
			]))
		}
	}

	public static async deleteByTimes(times: string[]): Promise<void> {
		if (times.length === 0) return
		const placeholders = times.map(() => "?").join(",")
		await Database.db.exec(`delete from aurora where forecast_time in (${placeholders})`, times, "critical")
	}

	public static async getAll(): Promise<AuroraProperties[]> {
		const rows = await Database.db.exec(
			`select forecast_time, data_url, min_value, max_value, vertex_count, grid_resolution from aurora order by forecast_time desc`,
			[],
			"low"
		)
		return rows as AuroraProperties[]
	}

	public static async getByTimeRange(startTime: string, endTime: string): Promise<AuroraProperties[]> {
		const rows = await Database.db.exec(
			`select forecast_time, data_url, min_value, max_value, vertex_count, grid_resolution from aurora where forecast_time >= ? and forecast_time <= ? order by forecast_time desc`,
			[startTime, endTime],
			"low"
		)
		return rows as AuroraProperties[]
	}

	public static async getRecent(limit: number = 100): Promise<AuroraProperties[]> {
		const rows = await Database.db.exec(
			`select forecast_time, data_url, min_value, max_value, vertex_count, grid_resolution from aurora order by forecast_time desc limit ?`,
			[limit],
			"low"
		)
		return rows as AuroraProperties[]
	}

	public static async getAfterTime(afterTime: string): Promise<AuroraProperties[]> {
		const rows = await Database.db.exec(
			`select forecast_time, data_url, min_value, max_value, vertex_count, grid_resolution from aurora where forecast_time > ? order by forecast_time desc`,
			[afterTime],
			"low"
		)
		return rows as AuroraProperties[]
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as count from aurora`,
			[],
			"low"
		) as Array<{ count: number }>
		return rows[0]?.count ?? 0
	}
}

